import React, { useState } from 'react';
import API_URL from '../config/api';
import { useNavigate } from 'react-router-dom';
import AuthSkyBackground from '../components/AuthSkyBackground';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage('Password reset email sent! Please check your inbox.');
        // Navigate back to login with a success hint
        setTimeout(() => navigate('/login?reset=sent'), 800);
      } else {
        setError(data.message || 'Failed to send reset email');
      }
    } catch {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#03040e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        padding: '48px 20px',
      }}
    >
      <style>{`
        @keyframes floatCard { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3px)} }
        .forgot-input:focus { border-color: rgba(127,119,221,.55) !important; outline: none; }
        .forgot-input::placeholder { color: rgba(140,130,190,.28); }
        .forgot-btn-primary:hover:not(:disabled) { opacity: .87; }
        .forgot-link-btn:hover { color: rgba(200,190,240,.85) !important; }
        @media (prefers-reduced-motion: reduce) {
          .forgot-card { animation: none !important; }
        }
      `}</style>

      <AuthSkyBackground />

      <div
        className="forgot-card"
        style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          maxWidth: 400,
          background: 'rgba(8,12,28,.78)',
          border: '1px solid rgba(140,130,200,.18)',
          borderRadius: 22,
          padding: '42px 38px 38px',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          boxShadow: '0 24px 60px rgba(0,0,0,.35), 0 0 0 1px rgba(255,255,255,.03) inset',
          animation: 'floatCard 10s ease-in-out infinite',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 11, marginBottom: 10 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 11,
              background: 'linear-gradient(140deg,#7f77dd,#3c3489)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(80,70,180,.28)',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="4" fill="#ede9ff" />
              <circle cx="10" cy="3.5" r="2" fill="#ede9ff" opacity=".55" />
              <circle cx="16" cy="13.5" r="2" fill="#ede9ff" opacity=".55" />
              <circle cx="4" cy="13.5" r="2" fill="#ede9ff" opacity=".55" />
            </svg>
          </div>
          <span
            style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: 0.4,
              fontFamily: 'system-ui,sans-serif',
              background: 'linear-gradient(180deg,#ffffff 0%,#e8e5f5 55%,#b8b2d4 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 24px rgba(220,215,255,.15)',
            }}
          >
            ShahoSuite
          </span>
        </div>

        <h1
          style={{
            textAlign: 'center',
            fontSize: 20,
            fontWeight: 650,
            color: '#ede9ff',
            margin: '18px 0 8px',
            fontFamily: 'system-ui,sans-serif',
            letterSpacing: 0.2,
          }}
        >
          Forgot password?
        </h1>
        <p
          style={{
            textAlign: 'center',
            fontSize: 13,
            color: 'rgba(160,150,210,.75)',
            marginBottom: 28,
            fontFamily: 'system-ui,sans-serif',
            lineHeight: 1.5,
          }}
        >
          Enter your account email and we&apos;ll send you a secure link to reset your password.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 15 }}>
            <label
              style={{
                display: 'block',
                fontSize: 11.5,
                fontWeight: 600,
                color: '#cfc9e8',
                marginBottom: 7,
                textTransform: 'uppercase',
                letterSpacing: 0.9,
                fontFamily: 'system-ui,sans-serif',
              }}
            >
              Email
            </label>
            <input
              className="forgot-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={loading || !!message}
              style={{
                width: '100%',
                padding: '12px 15px',
                background: 'rgba(255,255,255,.04)',
                border: '1px solid rgba(120,110,190,.18)',
                borderRadius: 10,
                color: '#ede9ff',
                fontSize: 15,
                fontFamily: 'system-ui,sans-serif',
                boxSizing: 'border-box',
                opacity: loading || message ? 0.7 : 1,
              }}
            />
          </div>

          {error && (
            <div
              style={{
                color: '#f09595',
                fontSize: 13,
                textAlign: 'center',
                marginBottom: 12,
                fontFamily: 'system-ui,sans-serif',
                background: 'rgba(226,75,74,.1)',
                padding: '10px 14px',
                borderRadius: 8,
                border: '1px solid rgba(226,75,74,.2)',
              }}
            >
              {error}
            </div>
          )}

          {message && (
            <div
              style={{
                color: '#7ddea8',
                fontSize: 13,
                textAlign: 'center',
                marginBottom: 12,
                fontFamily: 'system-ui,sans-serif',
                background: 'rgba(47,196,146,.1)',
                padding: '10px 14px',
                borderRadius: 8,
                border: '1px solid rgba(47,196,146,.25)',
              }}
            >
              {message}
            </div>
          )}

          <button
            type="submit"
            className="forgot-btn-primary"
            disabled={loading || !!message}
            style={{
              width: '100%',
              padding: 13,
              marginTop: 6,
              background: 'linear-gradient(135deg,#7f77dd 0%,#534ab7 100%)',
              border: 'none',
              borderRadius: 11,
              color: '#fff',
              fontSize: 15,
              fontWeight: 600,
              cursor: loading || message ? 'default' : 'pointer',
              fontFamily: 'system-ui,sans-serif',
              letterSpacing: 0.3,
              transition: 'opacity .2s',
              opacity: loading || message ? 0.65 : 1,
            }}
          >
            {loading ? 'Sending reset link…' : message ? 'Email sent' : 'Send reset link'}
          </button>

          <button
            type="button"
            className="forgot-link-btn"
            onClick={() => navigate('/login')}
            style={{
              width: '100%',
              marginTop: 18,
              background: 'none',
              border: 'none',
              fontSize: 12.5,
              color: 'rgba(200,194,230,.7)',
              cursor: 'pointer',
              fontFamily: 'system-ui,sans-serif',
              transition: 'color .2s',
            }}
          >
            ← Back to login
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
